import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Plus, Search, Settings as SettingsIcon } from "lucide-react";
import { BrandAvatar } from "./BrandAvatar";

export type StorefrontDiscoveryStatus =
  | "none"
  | "discovering"
  | "done"
  | "failed";

export type BrandOption = {
  id: string;
  name: string;
  storefrontUrl?: string | null;
  storefrontDiscoveryStatus?: StorefrontDiscoveryStatus;
  brandRegistry: boolean;
  brandRegistryAnsweredAt: string | null;
  collaboratorStatus: "none" | "invite_sent" | "verified";
  inviteSentAt: string | null;
  verifiedAt: string | null;
};

export function BrandSwitcher({
  value,
  onChange,
  brands,
  canManage = false,
}: {
  value: string;
  onChange: (id: string) => void;
  brands: BrandOption[];
  canManage?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const active = brands.find((b) => b.id === value) ?? brands[0];

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.name.toLowerCase().includes(q));
  }, [brands, query]);

  function pick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      const target = filtered[highlight];
      if (target) {
        e.preventDefault();
        pick(target.id);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  if (!active) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-900 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
      >
        <BrandAvatar name={active.name} size="sm" />
        <span className="max-w-[180px] truncate">{active.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKey}
              placeholder="Search brands…"
              className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                No brands match.
              </div>
            )}
            {filtered.map((b, i) => (
              <button
                key={b.id}
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(b.id)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${
                  i === highlight ? "bg-slate-50 dark:bg-slate-800" : ""
                }`}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <BrandAvatar name={b.name} size="sm" />
                  <span className="truncate text-slate-900 dark:text-slate-100">{b.name}</span>
                </span>
                {b.id === value && (
                  <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-slate-100 py-1 dark:border-slate-800">
            <Link
              to="/dashboard/brands/new"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Create a new brand
            </Link>
            {canManage && (
              <Link
                to="/dashboard/brands"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <SettingsIcon className="h-4 w-4" />
                Manage brands
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
